import {match} from 'ts-pattern';

export type ProposedParagraphKind = 'unchanged' | 'changed' | 'removed';

export interface ProposedParagraph {
  /** The text, its kind and occurrence number: stable across renders, unique within a proposal. */
  key: string;
  text: string;
  /** `changed` is new or rewritten in the proposal; `removed` is in the current text only. */
  kind: ProposedParagraphKind;
}

const LINE_BREAK = /\r\n|\r|\n/;
const WHITESPACE_RUN = /\s+/g;

const toParagraphs = (text: string) =>
  text
    .split(LINE_BREAK)
    .map((line) => line.replace(WHITESPACE_RUN, ' ').trim())
    .filter((line) => line !== '');

/**
 * Lines up the current and the proposed paragraphs (longest common subsequence) so the preview can
 * show the proposal as text, mark what it rewrote, and keep a dropped paragraph visible (seen in
 * review: "Shorter" removed the greeting and the clean text did not show it).
 */
export const getChangedParagraphs = (previous: string, proposed: string): ProposedParagraph[] => {
  const before = toParagraphs(previous);
  const after = toParagraphs(proposed);
  const common = new Map<string, number>();
  const at = (i: number, j: number) => common.get(`${i},${j}`) ?? 0;

  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      common.set(
        `${i},${j}`,
        before.at(i) === after.at(j) ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1))
      );
    }
  }

  const result: {text: string; kind: ProposedParagraphKind}[] = [];
  let i = 0;
  let j = 0;
  while (i < before.length || j < after.length) {
    const step = match({
      isSame: i < before.length && j < after.length && before.at(i) === after.at(j),
      canRemove: i < before.length && (j >= after.length || at(i + 1, j) >= at(i, j + 1)),
    })
      .with({isSame: true}, () => 'unchanged' as const)
      .with({canRemove: true}, () => 'removed' as const)
      .otherwise(() => 'changed' as const);

    result.push({text: (step === 'removed' ? before.at(i) : after.at(j)) ?? '', kind: step});
    i += step === 'changed' ? 0 : 1;
    j += step === 'removed' ? 0 : 1;
  }

  return result.map((paragraph, index) => ({
    ...paragraph,
    key: `${paragraph.kind}:${paragraph.text}#${
      result
        .slice(0, index)
        .filter((other) => other.kind === paragraph.kind && other.text === paragraph.text).length
    }`,
  }));
};
